/*
 * DataGridWindow — the paging engine behind <vitesse-data-grid>, extracted into a
 * plain class so every promise it makes is pinned by a Node test (no browser, no
 * DOM). The widget keeps only what needs a browser — rendering, viewport
 * measurement, scrollTop compensation — and drives this engine with row indices.
 *
 * The engine owns a VIEWPORT-SIZED SLIDING WINDOW of pages over a remote,
 * cursor-paged source, and closes the four gaps the in-widget version had:
 *
 *  • BOOKMARK REPLAY (scroll-back without reverse cursors): 14 of the 15 drivers
 *    only ever hand out FORWARD cursors, so a page dropped off the top of the
 *    window used to be unreachable — scrolling up hit a wall. Every page records
 *    the (cursor, limit) it was fetched WITH; when a top page is dropped, that
 *    bookmark is kept. Scrolling up re-runs the exact same fetch, so the exact
 *    same page comes back — no driver changes. Above the oldest kept bookmark
 *    (only reachable past MAX_BOOKMARKS pages of history) the honest fallback is
 *    a restart from the first page.
 *
 *  • TRIM HYSTERESIS: pages are dropped only once the window exceeds
 *    cap × TRIM_FACTOR (then trimmed back to cap), so scrolling a little down
 *    and drifting back up never refetches rows discarded moments earlier.
 *
 *  • RETRY + WATCHDOG (a failure is never silent or final): a failed fetch sets
 *    status 'retrying' and re-plans with exponential backoff; a flight that
 *    never settles is superseded by a watchdog after FLIGHT_TIMEOUT_MS and takes
 *    the same retry path. ensure() also self-heals a wedged status. The engine
 *    can therefore never end up permanently stuck.
 *
 *  • DIRECTIONAL PREFERENCE: when both edges are within prefetch range (small
 *    windows), fetch in the direction the user is actually scrolling.
 */
/** Trim only past cap × this — the hysteresis band that stops boundary thrash. */
const TRIM_FACTOR = 1.5;
/** Replay bookmarks kept for pages dropped off the top. Bookmarks are tiny
 *  strings; at ~40 rows/page this is ~20k rows of back-history before the
 *  restart-from-top fallback can ever trigger. */
const MAX_BOOKMARKS = 512;
const RETRY_BASE_MS = 500;
const RETRY_MAX_MS = 5000;
/** A flight still unsettled after this long is presumed hung: it is superseded
 *  (its eventual landing dropped) and the failure/retry path taken instead. */
const FLIGHT_TIMEOUT_MS = 10_000;
export class DataGridWindow {
    constructor(opts) {
        this.opts = opts;
        this.pages = [];
        this.rowsFlat = [];
        /** Bookmarks of pages dropped off the TOP, oldest first (stack: pop = the
         *  page directly above the current window). */
        this.droppedAbove = [];
        /** True once bookmarks were evicted past MAX_BOOKMARKS — above them only the
         *  restart-from-top fallback remains. */
        this.lostAbove = false;
        this.statusValue = '';
        /** Bumped on reset AND on watchdog supersede — a landing with a stale id is
         *  dropped on the floor. */
        this.flightId = 0;
        this.flightLive = false;
        this.retryAttempts = 0;
        this.retryTimer = null;
        this.watchdogTimer = null;
        this.lastEnsure = null;
        this.lastFirstVisible = 0;
        this.lastDir = 'fwd';
        this.schedule = opts.schedule ?? ((fn, ms) => setTimeout(fn, ms));
        this.cancel = opts.cancel ?? ((h) => clearTimeout(h));
    }
    // ── read side ──
    get rows() {
        return this.rowsFlat;
    }
    get status() {
        return this.statusValue;
    }
    get atStart() {
        return this.droppedAbove.length === 0 && !this.lostAbove;
    }
    get atEnd() {
        return this.pages.length === 0 || this.pages[this.pages.length - 1].next === '';
    }
    // ── lifecycle ──
    /** Point the engine at a (new) source and reload from the first page. */
    attach(source) {
        this.source = source;
        this.reset();
    }
    /** Clear the window and reload from the first page; in-flight landings and
     *  armed timers from before the reset are dropped. */
    reset() {
        this.flightId++;
        this.flightLive = false;
        this.clearTimers();
        this.pages = [];
        this.rowsFlat = [];
        this.droppedAbove = [];
        this.lostAbove = false;
        this.statusValue = '';
        this.retryAttempts = 0;
        this.lastEnsure = null;
        this.lastFirstVisible = 0;
        this.lastDir = 'fwd';
        if (this.source)
            this.launch('fwd', '', this.opts.pageSize());
        else
            this.emit(0, 0);
    }
    dispose() {
        this.flightId++;
        this.flightLive = false;
        this.clearTimers();
    }
    /** The planning entry point: the widget reports the visible row range on every
     *  scroll frame (and after loads); the engine decides whether to fetch. */
    ensure(firstVisible, lastVisible, prefetchRows) {
        this.lastEnsure = { first: firstVisible, last: lastVisible, prefetch: prefetchRows };
        if (firstVisible !== this.lastFirstVisible) {
            this.lastDir = firstVisible > this.lastFirstVisible ? 'fwd' : 'bwd';
            this.lastFirstVisible = firstVisible;
        }
        // Self-heal: a status with no live flight and no armed retry is a wedge
        // (impossible by construction, but the whole point of a watchdog is the
        // states you didn't construct). Clear it and plan normally.
        if (this.statusValue !== '' && !this.flightLive && this.retryTimer === null) {
            this.statusValue = '';
        }
        if (this.flightLive || this.retryTimer !== null)
            return;
        if (!this.source)
            return;
        if (this.pages.length === 0) {
            this.launch('fwd', '', this.opts.pageSize());
            return;
        }
        const nearBottom = lastVisible >= this.rowsFlat.length - prefetchRows && !this.atEnd;
        const nearTop = firstVisible <= prefetchRows && !this.atStart;
        if (nearBottom && nearTop) {
            if (this.lastDir === 'bwd')
                this.launchBackward();
            else
                this.launchForward();
        }
        else if (nearBottom)
            this.launchForward();
        else if (nearTop)
            this.launchBackward();
    }
    // ── fetch machinery ──
    launchForward() {
        const cursor = this.pages[this.pages.length - 1]?.next ?? '';
        if (cursor === '')
            return;
        this.launch('fwd', cursor, this.opts.pageSize());
    }
    launchBackward() {
        const bm = this.droppedAbove[this.droppedAbove.length - 1];
        if (!bm) {
            // Scrolled above the oldest kept bookmark (only possible past
            // MAX_BOOKMARKS dropped pages): the honest fallback is a fresh start
            // from the first page — stale replay guesses would show wrong rows.
            if (this.lostAbove)
                this.reset();
            return;
        }
        // Replay with the RECORDED limit, not the current pageSize(): the viewport
        // may have been resized since, and only the original limit reproduces the
        // exact page so the seam with the window below has no gap and no overlap.
        this.launch('bwd', bm.cursor, bm.limit);
    }
    launch(dir, cursor, limit) {
        if (!this.source)
            return;
        const id = this.flightId;
        this.flightLive = true;
        this.statusValue = this.retryAttempts > 0 ? 'retrying' : 'loading';
        this.armWatchdog(id);
        this.emit(0, 0);
        this.source(cursor, limit).then((page) => this.land(id, dir, cursor, limit, page), () => this.fail(id));
    }
    land(id, dir, cursor, limit, page) {
        if (id !== this.flightId)
            return; // superseded by reset or watchdog
        this.flightLive = false;
        this.disarmWatchdog();
        this.statusValue = '';
        this.retryAttempts = 0;
        const landed = { rows: page.rows, next: page.nextCursor, bookmark: { cursor, limit } };
        let prepended = 0;
        let droppedTop = 0;
        if (dir === 'fwd') {
            this.pages = this.pages.length ? [...this.pages, landed] : [landed];
            droppedTop = this.trim('top');
        }
        else {
            this.droppedAbove.pop(); // the bookmark just replayed is back in the window
            prepended = landed.rows.length;
            this.pages = [landed, ...this.pages];
            this.trim('bottom');
        }
        this.rowsFlat = this.pages.flatMap((p) => p.rows);
        // No self-re-plan here: a landing SHIFTS row indices (prepend/drop), so the
        // last ensure()'s indices are stale — the widget re-plans after it has
        // compensated scroll and recomputed the visible range (see onEngineChange).
        this.emit(prepended, droppedTop);
    }
    /** Trim the far edge back to windowCap once past cap × TRIM_FACTOR. Returns
     *  rows dropped off the TOP (bottom trims don't shift the window's top edge). */
    trim(edge) {
        const cap = this.opts.windowCap();
        let total = this.pages.reduce((n, p) => n + p.rows.length, 0);
        if (total < cap * TRIM_FACTOR)
            return 0;
        let droppedTop = 0;
        if (edge === 'top') {
            while (this.pages.length > 1 && total - this.pages[0].rows.length >= cap) {
                const p = this.pages.shift();
                droppedTop += p.rows.length;
                total -= p.rows.length;
                this.droppedAbove.push(p.bookmark);
            }
            while (this.droppedAbove.length > MAX_BOOKMARKS) {
                this.droppedAbove.shift();
                this.lostAbove = true;
            }
        }
        else {
            while (this.pages.length > 1 && total - this.pages[this.pages.length - 1].rows.length >= cap) {
                total -= this.pages.pop().rows.length;
            }
        }
        return droppedTop;
    }
    fail(id) {
        if (id !== this.flightId)
            return;
        this.flightLive = false;
        this.disarmWatchdog();
        this.retryAttempts++;
        this.statusValue = 'retrying';
        const delay = Math.min(RETRY_BASE_MS * 2 ** (this.retryAttempts - 1), RETRY_MAX_MS);
        this.retryTimer = this.schedule(() => {
            this.retryTimer = null;
            // Re-PLAN rather than re-fire the identical fetch: if the user scrolled
            // away from the edge meanwhile, nothing is needed and nothing is fetched.
            const e = this.lastEnsure;
            if (e)
                this.ensure(e.first, e.last, e.prefetch);
            else if (this.pages.length === 0)
                this.launch('fwd', '', this.opts.pageSize());
        }, delay);
        this.emit(0, 0);
    }
    armWatchdog(id) {
        this.disarmWatchdog();
        this.watchdogTimer = this.schedule(() => {
            this.watchdogTimer = null;
            if (id !== this.flightId || !this.flightLive)
                return;
            // The flight hung. Supersede it (its landing, if ever, is stale now) and
            // take the normal failure path so backoff + re-plan apply.
            this.flightId++;
            this.flightLive = false;
            this.fail(this.flightId);
        }, FLIGHT_TIMEOUT_MS);
    }
    disarmWatchdog() {
        if (this.watchdogTimer !== null) {
            this.cancel(this.watchdogTimer);
            this.watchdogTimer = null;
        }
    }
    clearTimers() {
        this.disarmWatchdog();
        if (this.retryTimer !== null) {
            this.cancel(this.retryTimer);
            this.retryTimer = null;
        }
    }
    emit(prepended, droppedTop) {
        this.opts.onChange({ prepended, droppedTop });
    }
}
//# sourceMappingURL=data-grid-window.js.map